import React, { useCallback, useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { useLazyCommand, useDomainEvent } from '@soap/modules';

interface RemoveLessonDialogProps {
    unitId: string;
    lessonId: string;
    lessonTitle: string;
    onConfirm: () => void;
    onRemoved: () => void;
    onClose: () => void;
}

const viewStates = {
    ready: 0,
    submitting: 1
};

export function RemoveLessonDialog({
    unitId,
    lessonId,
    lessonTitle,
    onConfirm,
    onRemoved,
    onClose
}: RemoveLessonDialogProps) {
    const [viewState, setViewState] = useState(viewStates.ready);
    const [sendRemoveLessonCommand] = useLazyCommand('C332v1_RemoveLessonFromUnit');
    const triggerCloseContextMenu = useDomainEvent({
        eventName: 'close-context-menu'
    });

    const handleClose = useCallback(() => {
        setViewState(viewStates.ready);
        onClose();
        triggerCloseContextMenu();
    }, [onClose, triggerCloseContextMenu]);

    const handleRemove = async () => {
        setViewState(viewStates.submitting);
        onConfirm();

        try {
            await sendRemoveLessonCommand({
                C332_UnitId: unitId,
                C332_LessonId: lessonId
            });
            onRemoved();
            handleClose();
        } catch (error) {
            console.error('Error removing lesson:', error);
            setViewState(viewStates.ready);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-bold text-gray-900">Remove lesson</h2>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            disabled={viewState === viewStates.submitting}
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <p className="text-sm text-gray-700 mb-6">
                        Remove <strong>{lessonTitle}</strong> from this unit? This cannot be undone.
                    </p>

                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            disabled={viewState === viewStates.submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => void handleRemove()}
                            className="px-4 py-2 bg-destructive-action hover:bg-destructive-action-dark text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            disabled={viewState === viewStates.submitting}
                        >
                            <Trash2 size={16} className={`mr-2 ${viewState === viewStates.submitting ? 'animate-spin' : ''}`} />
                            {viewState === viewStates.submitting ? 'Removing...' : 'Remove'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
